/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import useSWR from "swr";

import { modalActions } from "../../modules/modal";
import { useSelector } from "../../store";
import { fetchTicketRedeemGroups } from "./fetchTicketRedeemGroups";
import { getEnabledModes, setEnabledModes } from "./qrcodeReaderEnabledModes";
import { getReaderId, setReaderId } from "./qrcodeReaderId";
import {
  getSelectedRedeemGroupIds,
  setSelectedRedeemGroupIds,
} from "./useSelectedRedeemTicketIds";

import type { TicketRedeemGroup } from "../../firestore/types";
import type { EnabledModes } from "./qrcodeReaderEnabledModes";

const QrcodeReaderSetting: React.FC = () => {
  const dispatch = useDispatch();
  const isOpenError = useSelector((state) => state.modal.isOpenError);
  const { data: redeemGroups, error: redeemGroupsError } = useSWR<
    TicketRedeemGroup[],
    Error
  >("redeemGroups", fetchTicketRedeemGroups);
  const [selectedIds, setSelectedIds] = useState<string[]>(
    getSelectedRedeemGroupIds
  );
  const [readerId, setReaderIdState] = useState<string>(getReaderId);
  const [enabledModes, setEnabledModesState] =
    useState<EnabledModes>(getEnabledModes);

  const handleReaderIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setReaderIdState(value);
    setReaderId(value);
  };

  const handleEnabledModeToggle = (key: keyof EnabledModes) => {
    setEnabledModesState((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      setEnabledModes(next);
      return next;
    });
  };

  useEffect(() => {
    if (redeemGroupsError && !isOpenError) {
      dispatch(
        modalActions.toggleError({
          msg: "もぎりグループの取得に失敗しました",
        })
      );
    }
  }, [redeemGroupsError, isOpenError, dispatch]);

  const handleToggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((v) => v !== id)
        : [...prev, id];
      setSelectedRedeemGroupIds(next);
      return next;
    });
  };

  return (
    <div css={styles.root}>
      <p>設定</p>
      <label css={styles.readerIdField}>
        <span css={styles.readerIdLabel}>リーダーID</span>
        <input
          type="text"
          value={readerId}
          onChange={handleReaderIdChange}
          placeholder="例: reader-001"
          css={styles.readerIdInput}
        />
      </label>
      <h2 css={styles.sectionHeading}>動作モード</h2>
      <label css={styles.item}>
        <input
          type="checkbox"
          checked={enabledModes.isChecked}
          onChange={() => handleEnabledModeToggle("isChecked")}
        />
        <span>チェックインを行う</span>
      </label>
      <label css={styles.item}>
        <input
          type="checkbox"
          checked={enabledModes.isRedeemed}
          onChange={() => handleEnabledModeToggle("isRedeemed")}
        />
        <span>特典引換を行う</span>
      </label>
      <h2 css={styles.sectionHeading}>もぎりグループ</h2>
      {redeemGroups === undefined ? (
        <p>読み込み中...</p>
      ) : (
        redeemGroups.map((group) => (
          <label key={group._id} css={styles.item}>
            <input
              type="checkbox"
              checked={selectedIds.includes(group._id)}
              onChange={() => handleToggle(group._id)}
            />
            <span>{group.name}</span>
          </label>
        ))
      )}
    </div>
  );
};

export default QrcodeReaderSetting;

const styles = {
  root: css({
    padding: 20,
    color: "#fff",
  }),
  item: css({
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 0",
    cursor: "pointer",
  }),
  readerIdField: css({
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "16px 0",
    maxWidth: 360,
  }),
  readerIdLabel: css({
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 0.5,
    color: "#ddd",
  }),
  readerIdInput: css({
    "&&": {
      width: "100%",
      padding: "10px 14px",
      fontSize: 15,
      lineHeight: 1.4,
      color: "#000",
      backgroundColor: "#fff",
      border: "1px solid #555",
      borderRadius: 6,
      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    },
    "&::placeholder": {
      color: "#aaa",
    },
    "&:focus": {
      borderColor: "#4a90e2",
      boxShadow: "0 0 0 3px rgba(74, 144, 226, 0.25)",
    },
  }),
  sectionHeading: css({
    margin: "24px 0 8px",
    paddingBottom: 6,
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: 0.5,
    color: "#ddd",
    borderBottom: "1px solid #444",
  }),
};
